import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { contactSection } from "@/i18n/contactSection";
import { getWhatsAppUrl } from "@/lib/contact";
import { mediaUrl } from "@/lib/mediaUrl";
import PortraitScatter from "./PortraitScatter";

const CLIENT_PHOTOS = [
  { name: "Melabiss", src: mediaUrl("/clients/melabiss.jpg") },
  { name: "السريع للتوصيل", src: mediaUrl("/clients/esseri.jpg") },
  { name: "محمد سعد الدوسري", src: mediaUrl("/clients/mohamed-al-dosari.jpg") },
];

const ClientFaces = () => (
  <div className="flex items-center ps-1">
    {CLIENT_PHOTOS.map(({ name, src }, index) => (
      <span
        key={src}
        className="-ms-1.5 flex size-8 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-[#dfe6e0] shadow-sm first:ms-0"
        style={{ zIndex: CLIENT_PHOTOS.length - index }}
      >
        <img src={src} alt={name} className="size-full object-cover" loading="eager" />
      </span>
    ))}
  </div>
);

const Hero = () => {
  const { t, lang } = useLanguage();
  const reduce = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, -42]);
  const leftCardY = useTransform(scrollYProgress, [0, 1], [0, -20]);
  const rightCardY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const ambientY = useTransform(scrollYProgress, [0, 1], [0, -24]);

  const fadeUp = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay: reduce ? 0 : delay, ease: [0.22, 1, 0.36, 1] },
  });

  return (
    <section ref={heroRef} id="top" data-testid="hero-section" className="hero-section relative isolate overflow-hidden bg-softwhite">
      <motion.div aria-hidden="true" className="hero-ambient pointer-events-none absolute inset-x-0 top-0 h-[48rem]" style={reduce ? undefined : { y: ambientY }} />
      <div aria-hidden="true" className="hero-grid pointer-events-none absolute inset-0 opacity-50" />

      <div className="relative mx-auto max-w-[90rem] px-5 pb-8 pt-28 sm:px-8 sm:pt-32 lg:pt-36">
        <div className="relative z-20 mx-auto max-w-5xl text-center">
          <p className="hidden text-xs font-semibold leading-6 text-sage-deep sm:block sm:text-sm">{t.hero.eyebrow}</p>
          <motion.h1
            initial={false}
            data-testid="hero-title"
            className="hero-title font-display mx-auto max-w-[21rem] text-[clamp(1.85rem,8vw,2.2rem)] font-bold tracking-[-0.045em] text-charcoal rtl:tracking-normal sm:mt-3 sm:max-w-[55rem] sm:text-[clamp(2rem,4.4vw,4.7rem)]"
          >
            <span className="block">{t.hero.title}</span>
            <span className="hero-title-accent block">{t.hero.titleAccent}</span>
          </motion.h1>

          <motion.p
            initial={false}
            data-testid="hero-subtitle"
            className="mx-auto mt-5 max-w-[21rem] text-[0.94rem] leading-7 text-graphite/80 sm:mt-3 sm:max-w-[40rem] sm:text-base"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div {...fadeUp(0)} className="relative z-30 mt-6 flex items-center justify-center gap-3 sm:mt-5">
            <a
              href={getWhatsAppUrl(contactSection[lang].whatsappMessage)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-primary-cta"
              className="hero-glass-cta hero-glass-cta-primary group inline-flex min-h-12 w-full max-w-[19rem] cursor-pointer items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-softwhite outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-4 sm:w-auto"
            >
              {t.hero.primary}
              <MessageCircle className="size-4 shrink-0" aria-hidden="true" />
            </a>
            <a
              href="#work"
              data-testid="hero-secondary-cta"
              className="hero-glass-cta hero-glass-cta-secondary group hidden h-11 cursor-pointer items-center gap-2 rounded-full px-6 text-sm font-semibold text-charcoal outline-none focus-visible:ring-2 focus-visible:ring-sage sm:inline-flex"
            >
              {t.hero.secondary}
              <ArrowDown className="size-4" aria-hidden="true" />
            </a>
          </motion.div>
          <p className="mx-auto mt-3 hidden max-w-xl text-xs leading-6 text-graphite/75 sm:block">{t.hero.reassurance}</p>
          <div data-testid="hero-project-proof" className="mt-4 hidden flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs leading-6 text-graphite/80 sm:flex">
            <span>{t.hero.proofLabel}</span>
            <a href="/projects/sadeed" className="rounded-sm font-semibold underline decoration-sage/60 underline-offset-4 hover:text-sage-deep focus-visible:ring-2 focus-visible:ring-sage">{t.hero.proofSadeed}</a>
            <a href="#work" className="rounded-sm font-semibold underline decoration-sage/60 underline-offset-4 hover:text-sage-deep focus-visible:ring-2 focus-visible:ring-sage">{t.hero.proofSaree}</a>
          </div>
        </div>

        <div className="hero-art relative mx-auto mt-8 h-[240px] max-w-[1000px] sm:mt-4 sm:h-[300px] lg:h-[clamp(240px,35svh,330px)]">
          <div aria-hidden="true" className="hero-art-halo pointer-events-none absolute inset-x-[16%] bottom-[1%] h-[68%] rounded-full" />
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-[60px] z-[1] hidden h-[170px] w-full overflow-visible min-[1120px]:block"
            viewBox="0 0 1000 170"
            preserveAspectRatio="none"
            fill="none"
          >
            <path d="M145 27 C212 54 177 133 365 164" stroke="#758a7b" strokeOpacity="0.48" strokeWidth="1.25" vectorEffect="non-scaling-stroke" />
            <path d="M855 27 C788 54 823 133 635 164" stroke="#758a7b" strokeOpacity="0.48" strokeWidth="1.25" vectorEffect="non-scaling-stroke" />
          </svg>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center lg:h-full">
            <PortraitScatter
              src={mediaUrl("/hero/yacoub-collage.png")}
              alt={t.hero.imageAlt}
              reduceMotion={reduce}
              y={portraitY}
            />
          </div>

          <motion.div
            style={reduce ? undefined : { y: leftCardY }}
            className="hero-client-card absolute start-0 top-[-28px] z-20 hidden w-40 min-[1120px]:block xl:start-8"
          >
            <motion.div
              animate={reduce ? undefined : { y: [0, -5, 0, 3, 0], rotate: [-5, -3.6, -5.2, -4.2, -5] }}
              transition={reduce ? undefined : { duration: 7.5, repeat: Infinity, ease: "easeInOut" }}
              className="-rotate-[5deg] rounded-[1.25rem] border border-white/90 bg-white/85 p-4 text-start shadow-[0_18px_45px_-32px_rgba(11,11,11,0.22)] backdrop-blur-xl"
            >
              <ClientFaces />
              <p className="mt-3 text-xs font-bold leading-4 text-charcoal">{t.hero.clientsTitle}</p>
              <p className="mt-1 text-[10px] leading-4 text-graphite/65">{t.hero.clientsNote}</p>
            </motion.div>
          </motion.div>

          <motion.div
            style={reduce ? undefined : { y: rightCardY }}
            className="hero-note-card absolute end-0 top-[-28px] z-20 hidden w-40 min-[1120px]:block xl:end-8"
          >
            <motion.div
              animate={reduce ? undefined : { y: [0, 3, 0, -5, 0], rotate: [5, 3.8, 5.3, 4.1, 5] }}
              transition={reduce ? undefined : { duration: 8.5, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
              className="rotate-[5deg] rounded-[1.25rem] border border-white/90 bg-white/85 p-4 text-start shadow-[0_18px_45px_-32px_rgba(11,11,11,0.22)] backdrop-blur-xl"
            >
              <span className="flex size-7 items-center justify-center rounded-full bg-mist text-sage-deep">
                <Sparkles className="size-4" strokeWidth={1.6} />
              </span>
              <p className="mt-3 text-xs font-bold leading-4 text-charcoal">{t.hero.noteTitle}</p>
              <p className="mt-1 text-[10px] leading-4 text-graphite/65">{t.hero.noteSub}</p>
            </motion.div>
          </motion.div>
        </div>
        <div data-testid="hero-project-proof-mobile" className="relative z-20 mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-xs leading-6 text-graphite/75 sm:hidden">
          <span>{t.hero.proofLabel}</span>
          <a href="/projects/sadeed" className="font-semibold underline decoration-sage/60 underline-offset-4 focus-visible:ring-2 focus-visible:ring-sage">{t.hero.proofSadeed}</a>
          <a href="#work" className="font-semibold underline decoration-sage/60 underline-offset-4 focus-visible:ring-2 focus-visible:ring-sage">{t.hero.proofSaree}</a>
        </div>
      </div>
      <div aria-hidden="true" className="hero-section-fade pointer-events-none absolute inset-x-0 bottom-0 h-32" />
    </section>
  );
};

export default Hero;
